import type { CSSProperties } from 'react';

export type SleepOverlayVariant = 'night-drift' | 'face';

export interface SleepOverlayProps {
  /**
   * Wall-clock wake time label, e.g. "06:30" — rendered as "Back at 06:30"
   * so a passerby reads the screen as scheduled-asleep, not faulty.
   * Omit to hide the label.
   */
  wakeAt?: string;
  /**
   * 0–1 progress through the sleep window; drives the thin bottom dwell bar
   * (time-until-wake). Omit to hide the bar.
   */
  wakeProgress?: number;
  /**
   * Visual treatment: 'night-drift' (breathing moon + star field, default)
   * or 'face' (the screen itself becomes a sleeping face — closed eyes,
   * small breathing mouth, floating Zzz — over the same star field).
   */
  variant?: SleepOverlayVariant;
}

/** Deterministic PRNG so star positions are stable across re-renders. */
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface Star {
  left: string;
  top: string;
  size: number;
  opacity: number;
  duration: string;
  delay: string;
}

const STAR_COUNT = 12;

/** Fixed star field — generated once at module load, never re-rolled. */
const STARS: Star[] = (() => {
  const rand = mulberry32(0x5eed);
  return Array.from({ length: STAR_COUNT }, () => {
    const duration = 22 + rand() * 16; // 22–38s per drift cycle
    return {
      left: `${4 + rand() * 92}%`,
      top: `${4 + rand() * 92}%`,
      size: 2 + Math.round(rand() * 2), // 2–4px
      opacity: 0.25 + rand() * 0.35, // 0.25–0.60 peak
      duration: `${duration.toFixed(1)}s`,
      // Negative delay desyncs the field so stars never drift in lockstep.
      delay: `${(-rand() * duration).toFixed(1)}s`,
    };
  });
})();

/**
 * Fullscreen "Night Drift" sleep overlay: near-black field, a slowly
 * breathing moon-dot in the upper third, and a sparse star field drifting on
 * independent 22–38s cycles. Everything is transform/opacity on a handful of
 * composited layers — no blur, no shadows, no video decoder — so a Pi 5 at
 * 15% backlight renders it for free. Deliberately dim: anything brighter
 * defeats the point of sleep mode.
 *
 * Mount it as a sibling of the playback stage (`absolute inset-0 z-40`,
 * same contract as RecycleOverlay) and suspend playback while it is up —
 * the parent owns mounting/unmounting and the brightness drop; this
 * component only draws the animation.
 */
export function SleepOverlay({ wakeAt, wakeProgress, variant = 'night-drift' }: SleepOverlayProps) {
  const progress =
    wakeProgress === undefined ? undefined : Math.min(1, Math.max(0, wakeProgress));

  return (
    <div data-testid="sleep-overlay" className="slp slp-enter absolute inset-0 z-40 overflow-hidden">
      <div className="slp-bg absolute inset-0" aria-hidden="true" />

      {/* Star field. */}
      {STARS.map((star, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="slp-star"
          style={
            {
              left: star.left,
              top: star.top,
              width: `${star.size}px`,
              height: `${star.size}px`,
              '--slp-star-opacity': star.opacity,
              animationDuration: star.duration,
              animationDelay: star.delay,
            } as CSSProperties
          }
        />
      ))}

      {variant === 'face' ? (
        <>
          {/* The screen itself is the face: serene closed eyes + a small
              open mouth, breathing as one group. Stroke-only SVG so it stays
              crisp at any panel resolution; the animation is transform-only. */}
          <div data-testid="sleep-face" className="slp-face" aria-hidden="true">
            <svg viewBox="0 0 100 64" className="slp-face-svg">
              <path className="slp-eye" d="M 8 20 Q 20 32 32 20" />
              <path className="slp-eye" d="M 68 20 Q 80 32 92 20" />
              <ellipse className="slp-mouth" cx="50" cy="50" rx="7" ry="5.5" />
            </svg>
          </div>
          {/* Floating Zzz drifting up and away, letters staggered. */}
          <div className="slp-zzz" aria-hidden="true">
            <span className="slp-z slp-z-1">Z</span>
            <span className="slp-z slp-z-2">z</span>
            <span className="slp-z slp-z-3">z</span>
          </div>
        </>
      ) : (
        /* Breathing moon-dot — the single focal point. */
        <div className="slp-moon" aria-hidden="true" />
      )}

      {/* Optional wake info: reads as "scheduled, not broken". */}
      {wakeAt ? (
        <div data-testid="sleep-wake-label" className="slp-wake-label">
          Back at {wakeAt}
        </div>
      ) : null}

      {progress !== undefined ? (
        <div className="slp-dwell" aria-hidden="true">
          <i
            data-testid="sleep-dwell-bar"
            style={{ transform: `scaleX(${progress})` }}
          />
        </div>
      ) : null}
    </div>
  );
}
